// client/src/App.tsx
import React, { useEffect, useState } from 'react';
import LgmProductNavigator from './pages/LgmProductNavigator';
import useAuth from 'auth/auth';

const App: React.FC = () => {
  const { instance, account, inProgress, roles } = useAuth();

  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'LGM Navigator';

    if (inProgress !== 'none') return;

    if (account) {
      const username = account.username || '';

      // ✅ Only allow @lgmpharma.com accounts with roles
      if (!username.toLowerCase().endsWith('@lgmpharma.com')) {
        setAuthError(
          '❌ Only LGM 365 accounts are allowed. You will be redirected.'
        );
        setTimeout(() => instance.logoutRedirect(), 3000);
        return;
      }

      if (roles.length === 0 || roles.includes('No roles assigned')) {
        setAuthError('❌ No role assigned. Please contact an administrator.');
        setTimeout(() => instance.logoutRedirect(), 3000);
        return;
      }
    }
  }, [instance, account, inProgress, roles]);

  if (inProgress !== 'none') {
    return (
      <div style={styles.loadingContainerStyle}>
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={styles.errorOverlayStyle}>
        <div style={styles.errorModalStyle}>
          <h2 style={styles.errorTitleStyle}>Access Denied</h2>
          <p style={styles.errorTextStyle}>{authError}</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div style={styles.loginOverlayStyle}>
        <div style={styles.loginModalStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Navigator"
            style={styles.loginLogoStyle}
          />
          <h1 style={styles.loginTitleStyle}>LGM Navigator</h1>
          <p style={styles.loginSubtitleStyle}>
            Smarter API Sourcing — Fast, Reliable, Global
          </p>
          <button
            style={styles.loginButtonStyle}
            onClick={() =>
              instance.loginRedirect({ scopes: ['openid', 'profile', 'email'] })
            }>
            🔒 Secure Login
          </button>
        </div>
      </div>
    );
  }

  // ✅ Only render the main navigator. Sub-pages (Grades / TherapeuticAreas)
  // are handled inside LgmProductNavigator.tsx based on navigation state.
  return <LgmProductNavigator />;
};

const styles: Record<string, React.CSSProperties> = {
  loadingContainerStyle: {
    height: '100vh', // h-screen
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb', // bg-gray-50
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  errorOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f9fafb', // bg-gray-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  errorModalStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-lg
    padding: '32px', // p-8
    textAlign: 'center' as const,
    maxWidth: '28rem', // max-w-md
  },
  errorTitleStyle: {
    fontSize: '20px', // text-xl
    fontWeight: 'bold',
    color: '#dc2626', // text-red-600
    marginBottom: '16px', // mb-4
  },
  errorTextStyle: {
    color: '#374151', // text-gray-700
  },
  loginOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f9fafb', // bg-gray-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  loginModalStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-lg
    padding: '48px', // p-12
    maxWidth: '32rem', // max-w-lg
    width: '100%',
    textAlign: 'center' as const,
  },
  loginLogoStyle: {
    height: '80px', // h-20
    margin: '0 auto 24px auto', // mx-auto mb-6
  },
  loginTitleStyle: {
    fontSize: '30px', // text-3xl
    fontWeight: 'bold',
    marginBottom: '16px', // mb-4
    color: 'var(--color-lgmPurple, #6b46c1)', // text-lgmPurple
  },
  loginSubtitleStyle: {
    fontSize: '18px', // text-lg
    color: '#4b5563', // text-gray-600
    marginBottom: '32px', // mb-8
  },
  loginButtonStyle: {
    backgroundColor: 'var(--color-lgmOrange, #f97316)', // bg-lgmOrange
    color: '#ffffff', // text-white
    padding: '12px 32px', // px-8 py-3
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    transition: 'background-color 0.2s', // transition
    fontSize: '18px', // text-lg
    fontWeight: '600', // font-semibold
    border: 'none',
    cursor: 'pointer',
  },
};

export default App;
